import { M_PER_DEG_LAT, type LatLng } from '@/core/utils';
import type { ConeResult } from './types';

/** A result before its summary (hits, longest path, timing) is known. */
export type ConeGrid = Omit<ConeResult, 'hits' | 'maxDist' | 'seconds'>;

/** Is cell k a usable result: reachable in 'fly', a possible takeoff in 'hike'? */
export function isHit(r: ConeGrid, k: number) {
  if (r.mode === 'fly') return !Number.isNaN(r.h[k]);
  return r.elev[k] >= r.need[k];
}

/** Flight path length from/to the origin, m. */
export function pathLength(r: ConeGrid, k: number) {
  return r.mode === 'fly' ? r.g[k] : r.launchG[k];
}

/** 'fly': height above ground on arrival at k. 'hike': height above the landing when arriving there. */
export function margin(r: ConeGrid, k: number) {
  return r.mode === 'fly' ? r.h[k] - r.elev[k] : r.elev[k] - r.launchG[k] / r.glideRatio - r.baseAlt;
}

/** Cells along the flight path, from cell k to the origin. */
export function pathCells(r: ConeGrid, k: number) {
  const cells = [k];
  for (let c = r.mode === 'fly' ? r.parent[k] : r.launchParent[k]; c >= 0; c = r.parent[c]) cells.push(c);
  return cells;
}

/** The clicked cell itself always counts, so a single hit means there is no flight at all. */
export const isEmpty = (r: ConeResult) => r.hits <= 1;

/** [lng, lat] of the centre of cell k. */
export function cellLngLat(r: ConeGrid, k: number): [number, number] {
  const i = k % r.size;
  const j = (k / r.size) | 0;
  return [
    r.origin.lng + ((i - r.halfSize) * r.cellSize) / r.mPerDegLng,
    r.origin.lat + ((r.halfSize - j) * r.cellSize) / M_PER_DEG_LAT,
  ];
}

/** Margin and path length at any point of the result, interpolated between the centres of the hit cells around it. */
export function sampleAt(r: ConeGrid, p: LatLng): { margin: number; path: number } | null {
  const x = ((p.lng - r.origin.lng) * r.mPerDegLng) / r.cellSize + r.halfSize;
  const y = r.halfSize - ((p.lat - r.origin.lat) * M_PER_DEG_LAT) / r.cellSize;
  const i0 = Math.floor(x);
  const j0 = Math.floor(y);
  let w = 0;
  let m = 0;
  let g = 0;
  // Bilinear weights over the four surrounding cells; cells outside the result are left out.
  for (const [i, j] of [
    [i0, j0],
    [i0 + 1, j0],
    [i0, j0 + 1],
    [i0 + 1, j0 + 1],
  ]) {
    if (i < 0 || j < 0 || i >= r.size || j >= r.size) continue;
    const k = j * r.size + i;
    if (!isHit(r, k)) continue;
    const wk = (1 - Math.abs(x - i)) * (1 - Math.abs(y - j));
    w += wk;
    m += wk * margin(r, k);
    g += wk * pathLength(r, k);
  }
  return w > 0 ? { margin: m / w, path: g / w } : null;
}

/** The result cell under a point, or -1. */
export function cellAt(r: ConeResult | null, p: LatLng): number {
  if (!r || isEmpty(r)) return -1;
  const { origin, halfSize: N, size, cellSize: cs, mPerDegLng } = r;
  const i = Math.round(((p.lng - origin.lng) * mPerDegLng) / cs) + N;
  const j = N - Math.round(((p.lat - origin.lat) * M_PER_DEG_LAT) / cs);
  if (i < 0 || j < 0 || i >= size || j >= size) return -1;
  const k = j * size + i;
  return isHit(r, k) ? k : -1;
}

export function summarize(r: ConeGrid) {
  let hits = 0;
  let maxDist = 0;
  for (let k = 0; k < r.size * r.size; k++) {
    if (!isHit(r, k)) continue;
    hits++;
    maxDist = Math.max(maxDist, pathLength(r, k));
  }
  return { hits, maxDist };
}
